import React, { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Avatar,
    Button,
    Chip,
    IconButton,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    useMediaQuery, useTheme,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import DoneIcon from '@mui/icons-material/Done';
import { ProductSummaryDto } from '@services';
import DateComponent from '../DateComponent/DateComponent';
import boddVariables from '../../../../variables.module.scss';
import GarmentIconSelectorComponent
    from '../GarmentIconSelectorComponent/GarmentIconSelectorComponent';

interface ProductTableComponentProps {
    products: ProductSummaryDto[];
}

const ProductTableComponent: FC<ProductTableComponentProps> = (props) => {
    const { products } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <TableContainer data-testid="ProductTableComponent" sx={{ flex: 1 }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Product Code</TableCell>
                        {!isMobile && (<TableCell>Product Name</TableCell>)}
                        <TableCell>Category Tags</TableCell>
                        <TableCell>Modified</TableCell>
                        <TableCell>Size Chart</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {products.map((p) => (
                        <TableRow key={p.id}>
                            <TableCell>
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    <Avatar
                                        src={p.imageUrl ?? undefined}
                                        variant="rounded"
                                        sx={{
                                            border: p.imageUrl ? 1 : 0,
                                            borderColor: boddVariables.grey100,
                                            bgcolor: `${boddVariables.brandBlue}1A`,
                                        }}
                                    >
                                        <GarmentIconSelectorComponent
                                            garment={`${p.garmentType?.toLowerCase()}Garment`}
                                            color={`${boddVariables.brandBlue}80`}
                                            fontSize="32px"
                                        />
                                    </Avatar>
                                    <Link component={RouterLink} to={`/products/${p.productCode}`}>
                                        {p.productCode}
                                    </Link>
                                </Stack>
                            </TableCell>
                            {!isMobile && (
                                <TableCell>
                                    {p.name}
                                </TableCell>
                            )}
                            <TableCell>
                                {(p.categoryTags || []).map((c, i) => (i <= 2
                                    ? <Chip label={c} key={c} size="small" sx={{ mr: 1 }} /> : null))}
                                {(p.categoryTags || []).length > 3 ? `+${(p.categoryTags || []).length - 3}` : null}
                            </TableCell>
                            <TableCell>
                                <DateComponent date={p.lastModified} />
                            </TableCell>
                            <TableCell sx={{ p: 0 }}>
                                {!p.hasSizing && (
                                    <Button
                                        component={RouterLink}
                                        to={`/products/${p.productCode}?tab=size-chart`}
                                        startIcon={<ErrorIcon />}
                                        color="error"
                                        variant="text"
                                    >
                                        Upload
                                    </Button>
                                )}
                                {!!p.hasSizing && (
                                    <Tooltip title="View Size Chart">
                                        <IconButton
                                            component={RouterLink}
                                            to={`/products/${p.productCode}?tab=size-chart`}
                                            color="success"
                                        >
                                            <DoneIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ProductTableComponent;
